import React, { useEffect, memo } from 'react';
import { compose, pick, prop } from 'ramda';
import { withProps } from 'recompose';
import { useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useGameConfig } from '@theme/js/components/common/Game/hooks';
import { useTranslations } from 'js/hooks/i18n';
import { log } from 'log';
import withGameDescription from 'js/components/popups/GamesInfoPopup/components/GameInfo/withGameDescription';
import GameInfoMobile from './GameInfoMobile/GameInfoMobile';
import GameInfoDesktop from './GameInfoDesktop/GameInfoDesktop';
import { useFetchGameDetails } from './hooks/useFetchGameDetails';
import { AnimationProvider } from './context/AnimationProvider';

export const GameInfoBare = ({
    image,
    name: gameNameFromCMS,
    funSupport,
    showFavorite,
    description,
    code,
    hideButtons,
    gameLaunchType,
    gamesYouMightLike,
    isNew,
    hot,
    popular,
    exclusive,
    descriptionGameProvider,
    longImg,
    liveLaunchAlias,
    demoModeSupport
}) => {
    const t = useTranslations();
    const { isMobile } = useDeviceSelector(pick(['isMobile']));

    const { gameDescription, gameName } = useFetchGameDetails({ code });

    const name = gameName || gameNameFromCMS;

    useEffect(() => () => {
        window.localStorage.removeItem('catName');
    }, []);

    if (isMobile) {
        return (
            <AnimationProvider>
                <GameInfoMobile
                    image={image}
                    name={name}
                    funSupport={funSupport}
                    showFavorite={showFavorite}
                    description={description}
                    code={code}
                    hideButtons={hideButtons}
                    gameLaunchType={gameLaunchType}
                    isNew={isNew}
                    hot={hot}
                    popular={popular}
                    exclusive={exclusive}
                    descriptionGameProvider={descriptionGameProvider}
                    longImg={longImg}
                    t={t}
                    gameDescription={gameDescription}
                />
            </AnimationProvider>
        );
    }

    /* For Desktop and Tablet mockup for now */
    return (
        <GameInfoDesktop
            image={image}
            name={name}
            funSupport={funSupport}
            showFavorite={showFavorite}
            description={description}
            code={code}
            hideButtons={hideButtons}
            gameLaunchType={gameLaunchType}
            gamesYouMightLike={gamesYouMightLike}
            descriptionGameProvider={descriptionGameProvider}
            gameDescription={gameDescription}
            t={t}
            liveLaunchAlias={liveLaunchAlias}
            demoModeSupport={demoModeSupport}
        />
    );
};

export const GameInfo = compose(
    withTranslations,
    withProps(({ data: { code } }) => {
        const isLoggedIn = useUserSelector(prop('isLoggedIn'));
        const {
            longImg: longImg,
            description: descriptionGameProvider,
            gameCode,
            images = {},
            funSupport,
            demoModeSupport,
            ...game
        } = useGameConfig(code);
        const longImage = longImg || !isLoggedIn && images.guest || images.base;

        if (!gameCode) {
            log('warn', `Game with code '${code}' can not be found`);
            return;
        }

        return {
            ...game,
            descriptionGameProvider,
            code: gameCode,
            image: !isLoggedIn && images.guest || images.base,
            showFavorite: isLoggedIn,
            gamesYouMightLike: [],
            funSupport: !(funSupport === 'unavailable' || funSupport === 'guest' && isLoggedIn),
            longImg: longImage,
            demoModeSupport: !(demoModeSupport === 'unavailable' || demoModeSupport === 'guest' && isLoggedIn)
        };
    }),
    withProps(withGameDescription(1500)),
    memo
)(GameInfoBare);
